import React from 'react';

import { Button } from '@/design-system/atoms/Button';
import { LazyIconArrowLeft } from '@/design-system/atoms/Icons/IconArrowLeft/Lazy';
import { LinkListItem } from '@/design-system/atoms/LinkListItem';
import { Loading } from '@/design-system/atoms/Loading';

import { useTermFinderContext } from '../../stores/termFinderStore';
import styles from './SearchResults.module.scss';

const emptyObj = {};

export const SearchResults = ({ analytics }) => {
    const { hideResultsButtonLabel, noResultsComponent, errorComponent } = useTermFinderContext(
        (s) => s.uiCopy,
    );

    const searchResults = useTermFinderContext((s) => s.searchResults ?? emptyObj);

    const onShowAccordion = useTermFinderContext((s) => s.onShowAccordion);
    const searchTerm = useTermFinderContext((s) => s.term);

    const noResultsComponentWithAnalytics = React.useMemo(() => {
        if (!React.isValidElement(noResultsComponent)) {
            return;
        }

        return React.cloneElement(noResultsComponent, {
            analytics: {
                ...analytics,
                interactionType: 'no_results_found',
                searchTerm: searchTerm,
            },
        });
    }, [noResultsComponent, analytics, searchTerm]);

    const handleBackButtonClick = React.useCallback(
        (e) => {
            e.preventDefault();
            onShowAccordion();
        },
        [onShowAccordion],
    );

    const normalizedLinkListItems = React.useMemo(() => {
        const linkListItemsProps = [...searchResults.data];

        linkListItemsProps.sort((a, b) =>
            a.label.localeCompare(b.label, undefined, { numeric: true }),
        );

        return linkListItemsProps.map((linkListItemProps) => (
            <LinkListItem
                key={linkListItemProps.id}
                classNameWrapper={styles['term-finder__link']}
                {...linkListItemProps}
                analytics={analytics ? { ...analytics, searchTerm: searchTerm } : undefined}
            />
        ));
    }, [searchResults.data, analytics, searchTerm]);

    return (
        <>
            <div className={styles['term-finder__back-button']}>
                <Button
                    label={hideResultsButtonLabel}
                    buttonStyle={Button.STYLE.TEXT}
                    iconComponent={LazyIconArrowLeft}
                    iconRight={false}
                    onClick={handleBackButtonClick}
                />
            </div>

            {(() => {
                if (searchResults.error) {
                    return (
                        <div className={styles['term-finder__no-results']}>{errorComponent}</div>
                    );
                }

                if (searchResults.loading) {
                    return <Loading />;
                }

                if (normalizedLinkListItems && normalizedLinkListItems.length === 0) {
                    return (
                        <div className={styles['term-finder__no-results']}>
                            {noResultsComponentWithAnalytics}
                        </div>
                    );
                }

                if (normalizedLinkListItems && normalizedLinkListItems.length > 0) {
                    return <>{normalizedLinkListItems}</>;
                }

                return null;
            })()}
        </>
    );
};
